import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { transformHtmlContent } from 'util/transformHtmlContent';
import { ChevronsDown, ChevronsUp, Flag } from 'lucide-react';
import EntityActions from 'components/Entities/EntityActions'
import Report from 'components/Entities/Report'
import { Chip, Tooltip } from '@mui/joy'
import { Ellipsis } from 'lucide-react';
import { Issue } from 'util/constants'
import EntityThumbnail from 'components/Entities/EntityThumbnail'
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';

interface Props {
    issue: Issue
    showMeta: boolean
    truncateDescription: boolean | null
    isIssueProfilePage?: boolean
    showActions?: boolean
    onVote?: () => void
}

interface WikiSummary {
    thumbnail?: {
        source: string
        width: number
        height: number
    }
    originalimage?: {
        source: string
        width: number
        height: number
    }
    extract: string | null
}

const IssueCard = ({
    issue,
    showMeta = true,
    truncateDescription = true,
    isIssueProfilePage = false,
}: Props) => {
    const navigate = useNavigate()
    const { currentUser, setShowAuthModal, userIsMobile } = useUser();
    const [isExpanded, setIsExpanded] = useState(false)
    const issueHref = `/issue/${encodeURIComponent(issue.name.replace(/ /g, '_'))}`
    const [menuOpen, setMenuOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false)
    const [extract, setExtract] = React.useState<string | null>(null)
    const [imageUrl, setImageUrl] = React.useState<string | null>(null)
    const [isTruncated, setIsTruncated] = useState(false);
    const TruncateMarkup = require('react-truncate-markup').default;
    const [sharedImageHeight, setSharedImageHeight] = useState<number | null>(null);

    const IMAGE_CDN_URL = process.env.REACT_APP_IMAGE_CDN;

    useEffect(() => {
        const fetchSummary = async () => {
            if (issue.topic_id) {
                return;
            }
            try {
                const response = await fetch(
                    `https://en.wikipedia.org/api/rest_v1/page/summary/${issue.cname}`
                )
                const data: WikiSummary = await response.json()
                const imageSource =
                    data.thumbnail?.source || data.originalimage?.source || null
                setImageUrl(imageSource)
                setExtract(data.extract)
            } catch (error) {
                console.error('Error fetching Wikipedia summary:', error)
            }
        }

        fetchSummary()
    }, [issue.cname, extract, issue.topic_id])

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const element = event.target as HTMLElement;
        if (element.tagName.toLowerCase() === 'a') return;    
        if (element.classList.contains('read-more')) return;    
        if (element.classList.contains('card-more')) return;
        if (!isIssueProfilePage) {
            event.stopPropagation();
            navigate(issueHref);
        } else {          
            event.preventDefault();  
            if (element.tagName.toLowerCase() === 'img' || element.tagName.toLowerCase() === 'svg' || element.tagName.toLowerCase() === 'path' || element.classList.contains('no-metadata-link')) {
                event.stopPropagation();
                window.open(issue.wikipedia_link, '_blank', 'noopener,noreferrer');
            } else {            
                return;
            }
        }
    };
    
    const handleDescriptionClick = (event: React.MouseEvent) => {
        if (!truncateDescription || !isTruncated) return;
        event.preventDefault();
        if (isIssueProfilePage && truncateDescription) {
            setIsExpanded(!isExpanded);
        } else {
            return;
        }
    };
    
    const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setIsExpanded(!isExpanded);
        event.stopPropagation();
    };

    const handleReportClick = () => {
      if (!currentUser) {
          setShowAuthModal(true)
      } else {
          setMenuOpen(false)
          setDrawerOpen(false)
          setIsReportModalOpen(true)
      }
    }

    const MoreButton = React.forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className="card-more size-7 rounded-full flex justify-center items-center"
        >
            <Ellipsis className="icon-stroke size-5"/>
        </button>
    ))
    MoreButton.displayName = 'MoreButton'

    const MoreOptions = () => (
        <>
            {!userIsMobile ? (
                <div className="flex flex-col">
                    <DropdownMenuItem onClick={handleReportClick}>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                            <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                            <span>Report</span>
                        </div>
                    </DropdownMenuItem>
                </div>
            ) : (
                <>
                    <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                        <Flag className="size-4"/>
                        <span>Report</span>
                    </button>
                </>
            )}
        </>
    )

    const truncateEllipsis = (node: any) => {
        return (
            <span className="read-more">
                <button 
                    className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                    onClick={handleReadMoreClick}
                >
                    <span className="flex justify-center items-center ">
                        <ChevronsDown className="flex justify-center items-center"/>
                    </span>
                </button>
            </span>
        );
    };
    
    const renderDescription = (isSubject: boolean) => {
        if (truncateDescription && !isExpanded) {
          return (
            <TruncateMarkup 
                lines={9} 
                tokenize="words" 
                ellipsis={truncateEllipsis} 
                onTruncate={(wasTruncated: any) => setIsTruncated(wasTruncated)}
            >
              <div>
                {isSubject ? transformHtmlContent(issue.properties?.subject_properties?.extract || issue.properties?.wiki_summary || '') : transformHtmlContent(issue.properties?.topic_properties?.extract || '')}
              </div>
            </TruncateMarkup>
          );
        } else {
            return (
                <div>
                    {isSubject ? transformHtmlContent(issue.properties?.subject_properties?.extract || issue.properties?.wiki_summary || '') : transformHtmlContent(issue.properties?.topic_properties?.extract || '')}
                    {isTruncated && (
                        <span className="flex">
                            <button 
                                className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                                onClick={handleReadMoreClick}
                            >
                                <span className="flex justify-center items-center ">
                                    <ChevronsUp className="flex justify-center items-center" />
                                </span>
                            </button>
                        </span>
                    )}
                </div>
            );
        }
    };

    const handleImageHeight = (height: number) => {
        setSharedImageHeight(prev => {
            if (prev === null || height < prev) {
                return height;
            }
            return prev;
        });
    };

    return (
        <div className="card-container my-1">
            {isReportModalOpen && (
            <Report
                entityId={issue.id}
                entityType="issue"
                open={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
            />
            )}
            <article>
                <div className={`rounded-lg flex flex-col px-2 md:px-3 py-1 relative ${
                    isIssueProfilePage ? 'card-main-profile-page' : 'card-main'
                }`}>
                    <div className="entity-actions-container order-last">
                        <EntityActions
                            entity={issue}
                            entityType="issue"
                        />
                    </div>

                    <div className="card-content order-first">
                        {/* Title + More Icon */}
                        <div className="card-header flex flex-row items-center justify-between">
                            {isIssueProfilePage ? (
                                <span className="card-title">
                                    {issue.name}
                                </span>
                            ) : (
                                <Link to={issueHref} className="card-title">
                                    {issue.name}
                                </Link>
                            )}
                            <div className="relative">
                                {userIsMobile ? (
                                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                        <DrawerTrigger asChild>
                                            <MoreButton />
                                        </DrawerTrigger>
                                        <DrawerContent title="Actions">
                                            <MoreOptions />
                                        </DrawerContent>
                                    </Drawer>
                                ) : (
                                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                        <DropdownMenuTrigger asChild>
                                            <MoreButton />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <MoreOptions />
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        </div>

                        {/* Tags/Chips row + Created Timestamp */}
                        <div className="card-tags flex flex-row gap-2 my-2 items-center">
                            <Chip
                                component={'div' as any}
                                label="Issue"
                                color="neutral"
                                variant="soft"
                                size="md"
                                sx={{
                                    backgroundColor: '#cf1f1f',
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                }}
                            >
                                Issue
                            </Chip>
                            {issue.properties?.type && (
                                
                            <Tooltip title="AI-generated" arrow>
                                <Chip
                                    component={'div' as any}
                                    label={issue.properties.type}
                                    color="neutral"
                                    variant="soft"
                                    size="md"
                                    sx={{
                                        backgroundColor: '#555555',
                                        color: '#fff',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {issue.properties.type}
                                </Chip>
                            </Tooltip>
                            )}
                            <Tooltip title={formatDate(issue.created_at)} arrow>
                            <span className="card-date font-normal">
                                {formatRelativeTime(issue.created_at)}
                            </span>
                            </Tooltip>
                        </div>

                        {/* Description + Thumbnail */}
                        <div className="card-description flex flex-col-reverse md:grid md:grid-cols-3 md:grid-flow-row gap-2" 
                            onClick={handleCardClick}>
                            <div className={`md:col-span-2 ${issue.topic_id ? 'grid grid-cols-2 md:grid-rows-2 md:grid-cols-1 gap-2 md:gap-4' : ''} ${isExpanded ? 'grid-rows-1' : ''}`}>
                                <span onClick={handleDescriptionClick}>
                                    {renderDescription(true)}
                                </span>
                                {/* Wikipedia link */}
                                {issue.wikipedia_link && (
                                    <div className="hidden md:flex my-4">
                                        <a
                                            className="external-link text-xs line-clamp-2"
                                            href={issue.wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {issue.wikipedia_link}
                                        </a>
                                    </div>
                                )}
                                {issue.topic_id && (
                                    <span onClick={handleDescriptionClick}>
                                        {renderDescription(false)}
                                    </span>
                                )}

                                {issue.wikipedia_link && (
                                    <div className="flex md:hidden my-4">
                                        <a
                                            className="external-link text-xs line-clamp-2"
                                            href={issue.wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {issue.wikipedia_link}
                                        </a>
                                    </div>
                                )}

        
                                {/* Wikipedia link */}
                                {issue.topic_wikipedia_link && (
                                    <div className="flex my-4">
                                        <a
                                            className="external-link text-xs line-clamp-2"
                                            href={issue.topic_wikipedia_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {issue.topic_wikipedia_link}
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className={`md:col-span-1 hover:cursor-pointer ${issue.topic_id ? 'grid grid-cols-2 md:grid-rows-2 md:grid-cols-1 gap-4' : 'flex flex-row'}`}>
                                {(issue.properties?.subject_properties?.thumbnail?.source || issue.properties?.subject_properties?.wiki_parse_first_img || issue.properties?.reference_link_img_key || imageUrl) && (
                                <EntityThumbnail 
                                    url={issue.properties?.subject_properties?.thumbnail?.source 
                                        || issue.properties?.subject_properties?.wiki_parse_first_img 
                                        || issue.properties?.reference_link_img_key 
                                        || `${IMAGE_CDN_URL}/${issue.properties.reference_link_img_key}` 
                                        || imageUrl
                                        || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'
                                    }
                                    urlType="img"
                                    isProfilePage={isIssueProfilePage}
                                    onCardClick={handleCardClick}
                                    referenceLink={issue.wikipedia_link}
                                    onHeightChange={handleImageHeight}
                                    maxHeight={sharedImageHeight || undefined}
                                />
                                )}
                                {issue.topic_id && (issue.properties?.topic_properties?.thumbnail?.source || issue.properties?.topic_properties?.wiki_parse_first_img) && (
                                    <EntityThumbnail 
                                        url={issue.properties.topic_properties?.thumbnail?.source || issue.properties?.topic_properties?.wiki_parse_first_img || 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'}
                                        urlType="img"
                                        isProfilePage={isIssueProfilePage}
                                        onCardClick={handleCardClick}
                                        referenceLink={issue.topic_wikipedia_link}
                                        onHeightChange={handleImageHeight}
                                        maxHeight={sharedImageHeight || undefined}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </article>
        </div>
    )
}

export default IssueCard
